/**
 * WebwinkelKeur score + count, resolved for display.
 *
 * The homepage authority pillar showed a hardcoded "9,0" and a hardcoded
 * count of 1375 from the day it was written. Both move: the live figures
 * on 2026-08-04 were 1380 reviews at 9.005875942029. A number that is
 * stated on the page and wrong on the shop is the exact class of claim
 * that has to come from the source of truth, so it now comes from OUR
 * backend (`GET /api/public/webwinkelkeur/summary`) and the old constants
 * survive only as the fallback.
 *
 * This module is deliberately dependency-free so scripts/test-review-
 * surfaces.mjs can import it through the ts-alias hook and exercise the
 * real rounding and the real fallback rule, rather than string-matching
 * the component.
 *
 * Rules:
 * - The score renders through Intl for the storefront locale: 9,0 on
 *   nl/de/fr/es/it, 9.0 on en. Never a hand-written comma swap.
 * - ratingAverage is a 0-10 scale, rounded to one decimal for display.
 *   The scale is part of the contract, so it is CHECKED, not assumed: see
 *   MIN_LIVE_RATING below.
 * - The count is returned as a NUMBER. The caller passes it to next-intl
 *   as an ICU value (`t("reviewsLabel", { count })`) so the thousands
 *   separator and the noun both follow the locale. Building the string
 *   here would reintroduce the "1.375 reviews" literal that
 *   scripts/test-hardcoded-copy.mjs pins as must-be-absent.
 * - Score and count fall back TOGETHER. A half-live pillar (live count,
 *   stale score) would be a claim no source ever made.
 */

/** What the homepage showed as a literal before this module existed. */
export const FALLBACK_REVIEW_SCORE = 9;
export const FALLBACK_REVIEW_COUNT = 1375;

/**
 * The band a live 0-10 rating is allowed to land in.
 *
 * The floor is the load-bearing half. A resolver that accepts anything
 * above zero cannot tell a 0-10 average from a 5-point one, so the day a
 * backend normalizes to 5 stars, a 4.5 renders as "4,5" beside a gold
 * star and the shop silently advertises HALF its real rating on its own
 * homepage. That is not a display bug, it is a false claim, and no
 * exception fires. Five is chosen because it is the highest value a
 * 5-point scale can produce, so anything a 5-point backend could send
 * lands on the fallback instead of on the page.
 *
 * Honest limitation, stated rather than glossed: if a sub-5 reading were
 * ever GENUINE, the fallback shows 9,0 and that is also untrue. The bet
 * is on which cause is likelier. This shop's average is 9.0 over 1380
 * reviews; dragging that below 5.0 needs roughly half the review base to
 * turn into zeros, which is not drift, it is a catastrophe or corrupt
 * data. A changed scale is the far likelier explanation, and it is the
 * one that fails silently. The same limitation already applies to every
 * other fallback here, including the null case that is live today.
 *
 * The ceiling has slack because the value is an AVERAGE: the live figure
 * is 9.005875942029 and a perfect run of tens can float to 10.0000001,
 * which a hard `<= 10` sends to the fallback for a rounding artifact.
 * 10.5 absorbs the artifact and still rejects a different scale.
 */
const MIN_LIVE_RATING = 5;
const MAX_LIVE_RATING = 10.5;

/**
 * Upper bound on a review count. WebwinkelKeur's own figure is 1380 and
 * this shop has been trading since 2010; a million reviews is not a
 * number this tenant can reach, so it is a corrupt payload. Without the
 * bound `{ amount: 1e308 }` renders a 419-character integer into a
 * 12px pillar and destroys the row it sits in.
 */
const MAX_LIVE_COUNT = 1_000_000;

/** Response shape of GET /api/public/webwinkelkeur/summary. */
export interface WebwinkelkeurSummary {
  /** Total review count, e.g. 1380. */
  amount: number;
  /** Average rating on a 0-10 scale, e.g. 9.005875942029. */
  ratingAverage: number;
  /** ISO timestamp of the last successful upstream pull. */
  updatedAt: string;
}

export interface ResolvedReviewSummary {
  /** Locale-formatted, one decimal. "9,0" on nl, "9.0" on en. */
  score: string;
  /** Raw number for the ICU message. Never pre-formatted. */
  count: number;
  /** True when the live payload was usable. Lets callers tell the two
   *  states apart without re-deriving the guard. */
  isLive: boolean;
}

/** Locale used when the caller's is unusable. The storefront default. */
const DEFAULT_LOCALE = "nl";

const ONE_DECIMAL = {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
} as const;

/**
 * One decimal, localized. 9.005875942029 -> "9,0" on nl.
 * Intl rounds; we do not, so half-up behaviour matches every other
 * number on the storefront instead of a bespoke rule.
 *
 * An unusable locale falls back to the storefront default rather than
 * throwing. `Intl.NumberFormat("")` raises a RangeError, and this runs
 * inside a CLIENT component: an uncaught throw here does not degrade the
 * pillar, it takes the whole homepage into its error boundary. The route
 * that could produce an empty locale is guarded elsewhere (layout.tsx
 * calls notFound() first), which makes this unreachable today and exactly
 * the kind of thing that stops being unreachable in a later refactor.
 */
export function formatReviewScore(rating: number, locale: string): string {
  try {
    return new Intl.NumberFormat(locale, ONE_DECIMAL).format(rating);
  } catch {
    return new Intl.NumberFormat(DEFAULT_LOCALE, ONE_DECIMAL).format(rating);
  }
}

/**
 * Everything the backend can hand us that is not a usable summary maps
 * to the fallback: null (endpoint down, 503, not deployed yet), a
 * missing field, a non-number, NaN, a rating outside the 0-10 band this
 * module's contract names, a count that is not a plausible whole number
 * of reviews. The homepage never renders a blank, a zero, a halved
 * rating or a 419-digit count.
 *
 * The count must be an INTEGER. A fraction is not a number of reviews,
 * so it is a payload from a source that is not counting reviews, and
 * rounding it would launder that into a claim the page then makes out
 * loud. Falling back says the same thing honestly.
 */
export function resolveReviewSummary(
  summary: Partial<WebwinkelkeurSummary> | null | undefined,
  locale: string,
): ResolvedReviewSummary {
  const rating = summary?.ratingAverage;
  const amount = summary?.amount;

  const usable =
    typeof rating === "number" &&
    Number.isFinite(rating) &&
    rating >= MIN_LIVE_RATING &&
    rating <= MAX_LIVE_RATING &&
    typeof amount === "number" &&
    Number.isInteger(amount) &&
    amount > 0 &&
    amount < MAX_LIVE_COUNT;

  if (!usable) {
    return {
      score: formatReviewScore(FALLBACK_REVIEW_SCORE, locale),
      count: FALLBACK_REVIEW_COUNT,
      isLive: false,
    };
  }

  return {
    score: formatReviewScore(rating, locale),
    count: amount,
    isLive: true,
  };
}
